import React from 'react';
import { Link } from 'react-router-dom';
import PathCard, { IconFinancing, IconInvestors, IconSell } from '../components/PathCard';
import StepRow from '../components/StepRow';
import PartnerEntry from '../components/PartnerEntry';

/* ---------- small inline icons used in the trust bar ---------- */

const IconShield = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green)" strokeWidth="1.5">
    <path d="M12 2 L4 6 v6 c0 5 3.5 9 8 10 c4.5-1 8-5 8-10 V6 z" />
  </svg>
);
const IconCheckCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green)" strokeWidth="1.5">
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12 l3 3 l5-6" />
  </svg>
);
const IconNoFee = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green)" strokeWidth="1.5">
    <rect x="4" y="6" width="16" height="14" rx="2" />
    <path d="M8 6 V3 M16 6 V3 M4 11 h16" />
  </svg>
);

/* ----------------- page ------------------ */

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="oc-section oc-section--no-border" style={{ paddingTop: 96, paddingBottom: 80 }}>
        <div className="oc-shell">
          <div className="oc-max-820">
            <div className="oc-eyebrow oc-mb-3 oc-rise oc-rise-1">
              For security guard company owners
            </div>
            <h1 className="oc-display oc-rise oc-rise-2">
              Capital for your guard company.<br />
              On <em className="oc-em">your terms</em>.
            </h1>
            <p className="oc-lede oc-mt-3 oc-mb-4 oc-rise oc-rise-3">
              Whether you need a line of credit, a growth investor, or a buyer for the whole business — list once
              and connect with vetted capital partners. Anonymously, until you decide otherwise.
            </p>
            <div className="oc-row oc-rise oc-rise-3" style={{ gap: 8 }}>
              <a href="https://officercapital.com/register" className="oc-btn oc-btn--primary">List Your Company</a>
              <Link to="/how-it-works" className="oc-btn oc-btn--link">See how it works →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PATHS */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-mb-5">
            <div className="oc-eyebrow-gray oc-mb-2">Three reasons owners come to OfficerCapital</div>
            <h2 className="oc-h2">
              One listing. The right <em className="oc-em">conversation</em>.
            </h2>
          </div>

          <div className="oc-grid-3">
            <PathCard
              icon={<IconFinancing />}
              step="Path 01"
              title="You need financing"
              description="Working capital, payroll lines, factoring against receivables. Get in front of lenders who only finance guard companies."
              connectsTo="Lenders →"
              to="/financing"
            />
            <PathCard
              icon={<IconInvestors />}
              step="Path 02"
              title="You want an investor"
              description="Growth capital, minority partner, recap. Talk to investors who understand the unit economics of guarding before the first call."
              connectsTo="Investors →"
              to="/investors"
            />
            <PathCard
              icon={<IconSell />}
              step="Path 03"
              title="You're ready to sell"
              description="Full or majority sale. List anonymously, vet incoming acquirers, and keep your customers and team in the dark until you choose to engage."
              connectsTo="Acquirers →"
              to="/sell"
            />
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="oc-section oc-section--tight">
        <div className="oc-shell">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 48 }} className="oc-grid-trust">
            <div className="oc-row" style={{ gap: 14 }}>
              <IconShield />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--oc-ink)' }}>Anonymous by default</div>
                <div style={{ fontSize: 12, color: 'var(--oc-gray)' }}>Identity revealed only after you approve.</div>
              </div>
            </div>
            <div className="oc-row" style={{ gap: 14 }}>
              <IconCheckCircle />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--oc-ink)' }}>Every partner pre-vetted</div>
                <div style={{ fontSize: 12, color: 'var(--oc-gray)' }}>Manually approved, paid subscribers.</div>
              </div>
            </div>
            <div className="oc-row" style={{ gap: 14 }}>
              <IconNoFee />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--oc-ink)' }}>No fee to list</div>
                <div style={{ fontSize: 12, color: 'var(--oc-gray)' }}>You pay nothing to put a deal on the market.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">How it works</div>
              <h2 className="oc-h2">
                Three steps, on your <em className="oc-em">timeline</em>.
              </h2>
            </div>
            <p className="oc-lede" style={{ paddingTop: 14, fontSize: 16 }}>
              From listing to first capital partner conversation usually takes under two weeks.
              Nothing moves forward without you saying so.
            </p>
          </div>

          <div className="oc-grid-3-wide">
            <StepRow number="01" title="Build your listing">
              Share your headline metrics, financials, and what you're looking for — financing, an investor, or a sale.
              Your company name stays off the listing.
            </StepRow>
            <StepRow number="02" title="Review interested partners">
              Vetted lenders, investors, and acquirers request full access. You see who they are before they see who you are.
            </StepRow>
            <StepRow number="03" title="Approve and engage">
              Approve only the partners worth your time. Messaging opens. The rest of the marketplace stays anonymous.
            </StepRow>
          </div>
        </div>
      </section>

      {/* WHAT YOU CONTROL */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">What you control</div>
              <h2 className="oc-h2">
                Your name stays <em className="oc-em">yours</em> until you say otherwise.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                Whether you're shopping for working capital or weighing a sale, the conversation is sensitive.
                Customers, employees, and competitors don't need to know. OfficerCapital is built around that.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <ControlPoint
                  title="Anonymized financials"
                  body="Revenue, margin, officer count, geography. No names attached."
                />
                <hr className="oc-rule" />
                <ControlPoint
                  title="Per-partner access"
                  body="Approving one capital partner doesn't reveal you to anyone else."
                />
                <hr className="oc-rule" />
                <ControlPoint
                  title="Hidden customer list"
                  body="Customer names and contracts stay sealed until you choose to share."
                />
                <hr className="oc-rule" />
                <ControlPoint
                  title="Walk away anytime"
                  body="Pause or remove your listing in one click. No exit fees."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO'S ON THE OTHER SIDE */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-4">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Who's on the other side</div>
              <h2 className="oc-h2">
                Capital partners who've <em className="oc-em">earned</em> the seat.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              Every lender, investor, and acquirer is manually approved before they see a single listing.
              They pay a subscription, and they pay again to unlock any specific deal. Tire-kickers don't make it through.
            </p>
          </div>

          <div className="oc-grid-3">
            <PartnerCard
              label="Lenders"
              copy="Specialty factoring and working-capital lenders for guard companies. Underwrite faster with full financial access."
            />
            <PartnerCard
              label="Investors"
              copy="Growth equity, family offices, and PE firms looking for minority or recap deals in the security industry."
            />
            <PartnerCard
              label="Acquirers"
              copy="Strategic buyers and PE-backed roll-ups building security platforms. Looking for full sale or majority stake."
            />
          </div>
        </div>
      </section>

      {/* PRICING TEASE — Version B (message-only) */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Pricing for owners</div>
              <h2 className="oc-h2">
                Free to list. <em className="oc-em">Always.</em>
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                We don't charge owners to list, browse interest, or message. Capital partners carry the cost — they pay
                a subscription to access the marketplace and an unlock fee for any specific deal.
              </p>
              <div className="oc-mt-3">
                <Link to="/pricing" className="oc-btn oc-btn--link" style={{ paddingLeft: 0 }}>
                  See full pricing →
                </Link>
              </div>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <BenefitPoint
                  title="No listing fee"
                  body="List, edit, and remove your listing anytime at no cost."
                />
                <hr className="oc-rule" />
                <BenefitPoint
                  title="No success fee"
                  body="Close your financing or sale on whatever terms you negotiate. We don't take a cut."
                />
                <hr className="oc-rule" />
                <BenefitPoint
                  title="Discount for OfficerApps customers"
                  body="Capital partners pay 50% less to unlock your listing — meaning more of them will."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerEntry />
    </>
  );
}

/* ---------- small helpers used only on this page ---------- */

function ControlPoint({ title, body }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

function BenefitPoint({ title, body }) {
  return (
    <div>
      <div className="oc-row" style={{ gap: 10, marginBottom: 6 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green)" strokeWidth="1.5">
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12 l3 3 l5-6" />
        </svg>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)' }}>{title}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55, paddingLeft: 26 }}>{body}</div>
    </div>
  );
}

function PartnerCard({ label, copy }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>{copy}</p>
    </div>
  );
}
